/**
 * Hook Unificado de Permissões
 * 
 * Este hook é a API principal para verificação de permissões.
 * Sempre consulta permissionConfig diretamente (nunca permissions.features).
 * 
 * Use este hook em novos componentes ou ao migrar código legado.
 */

import { useMemo } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import {
  checkToolPermission,
  checkFeaturePermission,
  checkRoleBasedAccess,
  checkToolPermissions,
  checkFeaturePermissions,
  type PermissionConfig
} from '../lib/permissions/unifiedPermissionService';
import { PermissionResult } from './usePermissionCheck';
import { UserRole } from '../types/permissions';

/**
 * Hook principal para verificação unificada de permissões
 * 
 * Retorna funções e valores que sempre consultam permissionConfig diretamente.
 * Esta é a API recomendada para novos componentes.
 */
export function useUnifiedPermissions() {
  const { permissionConfig, userInfo, role } = usePermissions();
  
  const isAdmin = useMemo(() => !!userInfo?.isAdmin, [userInfo?.isAdmin]);
  
  /**
   * Verifica permissão de uma ferramenta específica
   */
  const checkTool = useMemo(() => {
    return (toolId: string): PermissionResult => {
      return checkToolPermission(toolId, permissionConfig, isAdmin);
    };
  }, [permissionConfig, isAdmin]);
  
  /**
   * Verifica permissão de uma feature/funcionalidade específica
   */
  const checkFeature = useMemo(() => {
    return (featureKey: string): PermissionResult => {
      return checkFeaturePermission(featureKey, permissionConfig, isAdmin);
    };
  }, [permissionConfig, isAdmin]);
  
  /**
   * Verifica acesso baseado em role
   */
  const checkRole = useMemo(() => {
    return (requiredRoles: UserRole[]): boolean => {
      return checkRoleBasedAccess(requiredRoles, role);
    };
  }, [role]);
  
  /**
   * Verifica múltiplas ferramentas de uma vez
   */
  const checkTools = useMemo(() => {
    return (toolIds: string[]): Record<string, PermissionResult> => {
      return checkToolPermissions(toolIds, permissionConfig, isAdmin);
    };
  }, [permissionConfig, isAdmin]);
  
  /**
   * Verifica múltiplas features de uma vez
   */
  const checkFeatures = useMemo(() => {
    return (featureKeys: string[]): Record<string, PermissionResult> => {
      return checkFeaturePermissions(featureKeys, permissionConfig, isAdmin);
    };
  }, [permissionConfig, isAdmin]);
  
  /**
   * Verifica se uma ferramenta está permitida (retorna apenas boolean)
   */
  const hasToolPermission = useMemo(() => {
    return (toolId: string): boolean => {
      return checkToolPermission(toolId, permissionConfig, isAdmin).allowed;
    };
  }, [permissionConfig, isAdmin]);
  
  /**
   * Verifica se uma feature está permitida (retorna apenas boolean)
   */
  const hasFeaturePermission = useMemo(() => {
    return (featureKey: string): boolean => {
      return checkFeaturePermission(featureKey, permissionConfig, isAdmin).allowed;
    };
  }, [permissionConfig, isAdmin]);
  
  return {
    // Funções de verificação detalhada
    checkTool,
    checkFeature,
    checkRole,
    checkTools,
    checkFeatures,
    
    // Funções de verificação simplificada (boolean)
    hasToolPermission,
    hasFeaturePermission,
    
    // Valores úteis
    isAdmin,
    role,
    permissionConfig,
    userInfo,
  };
}

/**
 * Hook simplificado para verificar uma ferramenta específica
 * Retorna apenas boolean (para compatibilidade com código legado)
 * 
 * @param toolId ID da ferramenta
 * @returns true se permitido, false caso contrário
 */
export function useUnifiedToolPermission(toolId: string): boolean {
  const { hasToolPermission } = useUnifiedPermissions();
  return hasToolPermission(toolId);
}

/**
 * Hook simplificado para verificar uma feature específica
 * Retorna apenas boolean (para compatibilidade com código legado)
 * 
 * @param featureKey Chave da feature
 * @returns true se permitido, false caso contrário
 */
export function useUnifiedFeaturePermission(featureKey: string): boolean {
  const { hasFeaturePermission } = useUnifiedPermissions();
  return hasFeaturePermission(featureKey);
}

/**
 * Hook para verificar múltiplas features de uma vez
 * Retorna Record com resultados detalhados
 * 
 * @param featureKeys Array de chaves de features
 * @returns Record com PermissionResult para cada feature
 */
export function useUnifiedFeaturePermissions(featureKeys: string[]): Record<string, PermissionResult> {
  const { checkFeatures } = useUnifiedPermissions();
  return checkFeatures(featureKeys);
}

/**
 * Hook para verificar múltiplas ferramentas de uma vez
 * Retorna Record com resultados detalhados
 * 
 * @param toolIds Array de IDs de ferramentas
 * @returns Record com PermissionResult para cada ferramenta
 */
export function useUnifiedToolPermissions(toolIds: string[]): Record<string, PermissionResult> {
  const { checkTools } = useUnifiedPermissions();
  return checkTools(toolIds);
}

/**
 * Função de diagnóstico global para validação durante testes
 * Expõe informações úteis para debugging no console do navegador
 * 
 * Uso: window.diagnosePermissions() no console do navegador
 */
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).diagnosePermissions = () => {
    // Esta função será implementada no PermissionContext para acessar estado atual
    console.log('📊 [DIAGNÓSTICO PERMISSÕES]');
    console.log('Use esta função dentro de um componente React que tenha acesso ao PermissionContext');
    console.log('Ou use: window.getPermissionDiagnostics() após carregar a página');
  };
  
  (window as any).getPermissionDiagnostics = () => {
    try {
      const tokenService = require('../lib/tokenService').TokenService;
      const userData = tokenService.getUserData();
      
      return {
        timestamp: new Date().toISOString(),
        hasUserData: !!userData,
        userInfo: userData?.data?.usuario ? {
          id: userData.data.usuario.id,
          nome: userData.data.usuario.nome,
          isAdmin: userData.data.isAdmin
        } : null,
        permissionsConfig: userData?.data?.permissionsConfig || [],
        permissions: userData?.data?.permissions || [],
        validationCounters: require('../lib/permissions/unifiedPermissionService').getValidationCounters()
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        message: 'Não foi possível obter diagnóstico de permissões'
      };
    }
  };
}
